/**
 * SectionMarker — a borderless, recessive section eyebrow.
 *
 * Ports the marketing website's section-header voice into the platform: a small
 * uppercase recessive label (the canonical `.text-eyebrow` kicker), an optional
 * mono-numeral watermark (15% white, soft glow — a drawn numeral, not a chip),
 * and an optional lit `.section-thread` hairline. Used to title a borderless
 * section with light + space instead of wrapping it in a `rounded-2xl border`
 * box. Consumes the premium-flow line vocabulary (materials.hairlineStrong) —
 * no parallel hairline family.
 *
 * Generic on purpose — any page that wants the calm "headline → air → quiet
 * recessive type → threaded sections" composition can reuse this. Not
 * Brain-specific.
 *
 * Reference: mystflo-website HowItWorks.astro step header.
 *
 * `action` (#3763 P2) adds the platform's section-doorway convention — a
 * trailing link/button after the thread, right-aligned in the same header
 * row (e.g. Pulse's "Needs attention ··· All activity →", mirroring
 * Billing's "Wallet activity ··· View all →" eyebrow row). Renders after the
 * thread in DOM order so it lands at the row's end without needing a flex
 * `order` trick.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface SectionMarkerProps {
  /** The recessive uppercase eyebrow label, e.g. "Operations". Accepts a
   *  ReactNode so a caller can compose a status color/leading dot into it
   *  (e.g. Pulse's lantern "Needs attention (2)") without a second prop. */
  label: ReactNode;
  /** Optional mono numeral watermark, e.g. "01". Rendered as a soft 15% glow. */
  numeral?: string;
  /** Render a lit hairline thread to the right of the label, filling the row. */
  thread?: boolean;
  /** Trailing doorway action (link/button) after the thread — requires `thread`. */
  action?: ReactNode;
  className?: string;
}

export function SectionMarker({
  label,
  numeral,
  thread = false,
  action,
  className,
}: SectionMarkerProps) {
  return (
    <div className={cn('flex items-center gap-3', className)}>
      {numeral && (
        <span
          aria-hidden
          // Mono numeral watermark — 15% white with a soft glow, matching the
          // website's step numerals. Decorative, so hidden from the a11y tree.
          className="font-mono text-[15px] font-medium leading-none tracking-tight text-white/15"
          style={{ textShadow: '0 0 30px rgba(255,255,255,0.08)' }}
        >
          {numeral}
        </span>
      )}
      <span className="text-eyebrow whitespace-nowrap">{label}</span>
      {thread && <span aria-hidden className="section-thread flex-1" />}
      {action}
    </div>
  );
}
